import React, { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { DownloadIcon, XIcon } from "../../components/icons";
import renderPdf from "../RenderPdf/RenderPdf";
import { useTranslation } from "react-i18next";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const DocumentPreviewModal = ({
  isOpen,
  onClose,
  document,
  onDownload,
  testId = "document-preview-modal",
}) => {
  const pdfCanvasRef = useRef(null);
  const { t } = useTranslation();

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && document) {
      setError(null);
      setPdfLoaded(false);
      setPdfLoading(document.type === "pdf");
    }
  }, [isOpen, document]);

  useEffect(() => {
    let mounted = true;
    let timeoutId;

    const loadPdf = async () => {
      if (!isOpen || !document || document.type !== "pdf") return;

      timeoutId = setTimeout(async () => {
        try {
          if (!pdfCanvasRef.current) {
            throw new Error("Canvas not available");
          }

          await renderPdf(document.url, pdfCanvasRef, 1.0);

          if (mounted) {
            setPdfLoaded(true);
            setPdfLoading(false);
          }
        } catch (err) {
          if (mounted) {
            setError("Failed to render PDF preview");
            setPdfLoading(false);
          }
        }
      }, 300);
    };

    loadPdf();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [isOpen, document]);

  if (!document) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            id={`${testId}-backdrop`}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto px-4">
          <div className="flex min-h-full items-center justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id={testId}
                className="w-full max-w-4xl max-h-[90vh] transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all flex flex-col"
              >
                <div className="flex items-center justify-between p-4 border-b">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {document.title || t("verifierApproval:document_preview")}
                  </Dialog.Title>

                  <div className="flex items-center gap-3">
                    <button
                      id={`${testId}-download`}
                      onClick={() => onDownload?.(document)}
                      className="flex items-center gap-1 text-sm font-medium text-cfPrimaryButton hover:text-cfPrimaryDark focus:outline-none"
                    >
                      <DownloadIcon size={16} className="inline-flex" aria-hidden />
                      {t("verifierApproval:download")}
                    </button>

                    <button
                      id={`${testId}-close`}
                      onClick={onClose}
                      aria-label={t("verifierApproval:close", {
                        defaultValue: "Close",
                      })}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <XIcon size={20} className="inline-flex" aria-hidden />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-auto bg-gray-50 p-4 flex items-center justify-center relative">
                  {pdfLoading && (
                    <div className="text-center text-gray-500">
                      <svg
                        className="animate-spin h-6 w-6 mx-auto mb-2 text-blue-600"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      <p>{t("verifierApproval:loading_pdf_preview")}</p>
                    </div>
                  )}

                  {error && (
                    <div className="text-center">
                      <p className="text-red-500 mb-3">{error}</p>
                      <button
                        id={`${testId}-open-new-tab`}
                        onClick={() =>
                          window.open(
                            document.url,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                        className="px-4 py-2 bg-cfPrimaryButton hover:bg-cfPrimaryDark text-white rounded-md"
                      >
                        {t("verifierApproval:open_in_new_tab")}
                      </button>
                    </div>
                  )}

                  {document.type === "image" && !error && (
                    <img
                      src={document.url}
                      alt={document.title}
                      className="max-w-full max-h-[70vh] object-contain rounded-lg shadow"
                      onError={() => setError("Failed to load image")}
                    />
                  )}

                  {document.type === "pdf" && (
                    <canvas
                      ref={pdfCanvasRef}
                      className={`${
                        pdfLoaded ? "block" : "hidden"
                      } max-w-full max-h-[65vh]`}
                      style={{ boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
                    />
                  )}
                </div>

                <div className="flex justify-end p-4 border-t bg-gray-50">
                  <button
                    id={`${testId}-cancel`}
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
                  >
                    {t("verifierApproval:close")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DocumentPreviewModal;
